import React, { Fragment, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import { Button, useDataProvider, useUnselectAll } from "react-admin";
import { fetchAllData } from "../../../dataProvider";
import { mergePurchaseInvoicePdfs } from "../tools";
import TableResultMergePurchaseInvoicePdf from "./TableResultMergePurchaseInvoicePdf";
import SaveIcon from "@material-ui/icons/Save";

interface IMergeAllPdfButton {
  statusIn: string[];
  subcontractorPurchaseInvoiceSummaryId: number;
}

interface IMergeAllPdfResults {
  id: number;
  description: string;
  status: string;
}

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  cancelButton: {
    backgroundColor: "#eeeeee",
    color: "black",
  },
  mergeButton: {
    backgroundColor: "#00bcd4",
    color: "white",
  },
});

const MergeAllPdfButton = ({
  statusIn,
  subcontractorPurchaseInvoiceSummaryId,
}: IMergeAllPdfButton) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [finished, setFinished] = useState(false);
  const [results, setResults] = useState<IMergeAllPdfResults[]>([]);
  const [invalidResults, setInvalidResults] = useState<IMergeAllPdfResults[]>(
    [],
  );
  const [processing, setProcessing] = useState(false);
  const [selectedPurchaseInvoices, setSelectedPurchaseInvoices] =
    useState<IMergeAllPdfResults[]>();
  const [totalPurchaseInvoices, setTotalPurchaseInvoices] = useState(0);

  const unselectAll = useUnselectAll();
  const provider = useDataProvider();
  const classes = useStyles();

  // Triggered when the "Merge All" button is clicked,
  // before open the confirmation dialog
  const handleOpen = async () => {
    setIsLoading(true);

    // get all purchase invoices matching the filters
    const purchaseInvoices = await fetchAllData(
      "subcontractorPurchaseInvoices",
      {
        StatusIn: statusIn,
        SubcontractorPurchaseInvoiceSummaryId:
          subcontractorPurchaseInvoiceSummaryId,
      },
    );

    // Store the total number of purchase invoices (including both valid and invalid ones)
    // It is used in the confirmation dialog UI
    setTotalPurchaseInvoices(purchaseInvoices.length);

    // Filter and mapped list of purchase invoices that have a valid PDF URL
    const mappedPurchaseInvoices = purchaseInvoices
      .filter(
        (data) =>
          data.pdfUrl && typeof data.pdfUrl === "string" && data.pdfUrl.trim(),
      )
      .map((data) => ({
        id: data.id,
        description: data.description,
        status: "Loading...",
      }));
    setSelectedPurchaseInvoices([...mappedPurchaseInvoices]);

    // Filter and mapped list of purchase invoices that are missing or have invalid PDF URLs
    const mappedPurchaseInvoicesWithNullPdf = purchaseInvoices
      .filter(
        (data) =>
          !data.pdfUrl ||
          typeof data.pdfUrl !== "string" ||
          !data.pdfUrl.trim(),
      ) // Filter only for null PDF URL
      .map((data) => ({
        id: data.id,
        description: data.description,
        status: "PDF link is empty or invalid",
      }));
    setInvalidResults([...mappedPurchaseInvoicesWithNullPdf]);

    setIsLoading(false);
    setOpen(true);
  };

  const handleClose = () => {
    if (finished) {
      unselectAll("subcontractorPurchaseInvoices");
      setResults([]);
      setFinished(false);
    }

    setOpen(false);
  };

  // Handles the merge process
  const handleMerge = async () => {
    setProcessing(true);

    await mergePurchaseInvoicePdfs({
      selectedPurchaseInvoices,
      provider,
      updateResults: setResults,
      delayMs: 500,
      revokeDelayMs: 60000, // revoke after 60 seconds
    });

    setFinished(true);
    setProcessing(false);
  };

  return (
    <Fragment>
      <Button
        label={isLoading ? "Please wait..." : "Merge All"}
        onClick={handleOpen}
        disabled={isLoading}
        className={classes.mergeButton}
      />

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="lg"
      >
        <DialogTitle id="alert-dialog-title">
          Merge All {statusIn.toString()} Purchase Invoice PDF
        </DialogTitle>
        <DialogContent>
          {results.length > 0 ? (
            <TableResultMergePurchaseInvoicePdf records={results} />
          ) : (
            <>
              <DialogContentText color="initial">
                Are you sure you want to merge all {statusIn.toString()}{" "}
                purchase invoice pdf?
              </DialogContentText>
              {invalidResults.length > 0 && (
                <>
                  <DialogContentText color="initial">
                    You are about to merge{" "}
                    <strong>
                      {selectedPurchaseInvoices.length} out of{" "}
                      {totalPurchaseInvoices}
                    </strong>{" "}
                    PDF.
                    <br />
                    Below is the list of purchase invoice item that don’t have
                    PDF link.
                  </DialogContentText>
                  <TableResultMergePurchaseInvoicePdf
                    records={invalidResults}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!finished && (
            <Button
              onClick={handleMerge}
              color="primary"
              label="Confirm"
              disabled={processing}
            >
              <SaveIcon />
            </Button>
          )}
          <Button
            onClick={handleClose}
            color="secondary"
            label={`${finished ? "Finish" : "Back"}`}
            disabled={processing}
            className={classes.cancelButton}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default MergeAllPdfButton;
